import { LogoColor } from '@vp/algo-design-logo-composer'
import { LogoTextColor } from '@vp/algo-design-logo-composer/lib/api/interfaces/studioInterfaces'
import { averageColors } from './colors'
import { LogoSignature, LogoSignatureType } from '../types/logo'
import { GetLogoResponse } from '../types/GetLogoResponse'
import { SIGNATURE_VARIANTS_API_LAYOUT_NAMES_MAPPING, ALL_LOGO_VARIANTS } from '../constants/brandBuilder'
import { LogoVariationName } from './createLogoVariantUrlGenerator'
import { IconSvgAssetExtended } from '../types/icon'
import { LayoutsOtherThanCircular } from './gradient'

export const getBackgroundColorFromSignature = (signature: LogoSignature): string => {
  return parseColor(signature.backgroundColor?.override ?? signature.backgroundColor.default)
}

export const getPrimaryTextColorFromSignature = (signature: LogoSignature): string => {
  return parseColor(signature.companyName.color?.override ?? signature.companyName.color.default)
}

export const getSecondaryTextColorFromSignature = (signature: LogoSignature): string => {
  if (signature.tagline.text.length > 0) {
    return parseColor(signature.tagline.color.override ?? signature.tagline.color.default)
  }

  return ''
}

export const getIconColorsFromSignature = (signature: LogoSignature): string[] => {
  if (isTextLogo(signature)) return []
  return signature.icon?.colors.map(color => parseColor(color.override || color.default)) || []
}

export const getMonogramColorsFromSignature = (signature: LogoSignature): string[] => {
  if (isTextLogo(signature)) return []
  if (signature.monogram) {
    return [
      parseColor(signature.monogram.backgroundColor.override || signature.monogram.backgroundColor.default),
      parseColor(signature.monogram.container.color.override || signature.monogram.container.color.default),
    ]
  } else {
    return []
  }
}

export const getTextColorsFromPaletteFromSignature = (signature: LogoSignature): string[] => {
  return [getPrimaryTextColorFromSignature(signature), getSecondaryTextColorFromSignature(signature)]
}

export const getBackgroundColorFromOriginalColorPaletteInSignature = (signature: LogoSignature): string => {
  return signature.colorConfig?.default.colorPaletteVariation.background || ''
}

export const getPrimaryTextColorFromOriginalColorPaletteInSignature = (signature: LogoSignature): string => {
  return signature.colorConfig?.default.colorPaletteVariation.primaryText || ''
}
export const getSecondaryTextColorFromOriginalColorPaletteInSignature = (signature: LogoSignature): string => {
  return signature.colorConfig?.default.colorPaletteVariation.secondaryText || ''
}

export const getIconColorsFromOriginalColorPaletteInSignature = (signature: LogoSignature): string[] => {
  return (
    signature.colorConfig?.default.colorPaletteVariation.icon[signature.colorConfig?.default.colorPaletteVariation.icon.length - 1]
      .colors || []
  )
}

export const isTextLogo = (signature: LogoSignature) => {
  return (signature.layout?.override || signature.layout?.default) === 'text_solid_container'
}

export const getTextContainerColorFromSignature = (signature: LogoSignature) => {
  if (isTextLogo(signature)) {
    return getColorFromLogoColor(signature.companyName.container!.color.override ?? signature.companyName.container!.color.default)
  }

  throw new Error('Not a text logo')
}

const getColorFromLogoColor = (color: LogoColor): string => {
  if (typeof color === 'string') {
    return color
  } else {
    return color.stopColors[0].color
  }
}

const parseColor = (color: LogoTextColor | LogoColor): string => {
  if (typeof color === 'string') {
    return color
  } else if (Array.isArray(color)) {
    return averageColors(color)
  } else {
    return averageColors([color.stopColors[0].color, color.stopColors[1].color])
  }
}

export const shouldShowBrandmark = (signature: LogoSignatureType['signature']): boolean => {
  if (isTextLogo(signature)) {
    return false
  }
  if (getPrimaryTextColorFromSignature(signature) === getBackgroundColorFromSignature(signature)) {
    if (signature.tagline.text.length === 0 || getSecondaryTextColorFromSignature(signature) === getBackgroundColorFromSignature(signature))
      return false
  }
  return true
}

export const shouldShowWordmark = (signature: LogoSignatureType['signature']): boolean => {
  if (getPrimaryTextColorFromSignature(signature) === getBackgroundColorFromSignature(signature)) {
    return false
  }
  return true
}

export const getLogoLayout = (signature: GetLogoResponse): string | undefined => {
  return (
    signature?.masterVariationData.composerSignature.layout?.override || signature?.masterVariationData.composerSignature.layout?.default
  )
}

export const getLogoLayoutVariantsToShowOnBrandBuilder = (signature: GetLogoResponse): LogoVariationName[] => {
  const originalLogoLayout = getLogoLayout(signature)
  const excludedVariant =
    originalLogoLayout &&
    SIGNATURE_VARIANTS_API_LAYOUT_NAMES_MAPPING[originalLogoLayout as keyof typeof SIGNATURE_VARIANTS_API_LAYOUT_NAMES_MAPPING]
  const finalLogoVariants = ALL_LOGO_VARIANTS.filter(variant => variant !== excludedVariant) as LogoVariationName[]
  return finalLogoVariants
}

export const getLogoSignatureFromAPIResponse = (logoData: GetLogoResponse) => {
  return logoData.masterVariationData ? logoData.masterVariationData?.composerSignature : logoData.variation?.primary?.composerSignature
}

export const getLogomakerLogoImageURL = (logoData: GetLogoResponse) => {
  return logoData?.masterVariationData
    ? logoData?.masterVariationData?.logoUploadInfo?.transparentLogoUploadInfo.uri
    : logoData?.variation?.submark?.imageURL
}

export const isPremium = (signature: LogoSignature): boolean => {
  const layout = signature?.layout?.override || signature?.layout?.default
  if (layout === LayoutsOtherThanCircular.TEXT_SOLID_CONTAINER) {
    return false
  }
  const iconData = signature.icon?.icon as IconSvgAssetExtended
  if (!iconData) {
    return false
  }
  return iconData.isPremium || false
}
